import React, {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Row, TableState } from 'react-table'
import cx from 'classnames'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'

import Autocomplete from 'common/components/autocomplete'
import Button, { ButtonProps } from 'common/components/button'
import FullScreen from 'common/components/FullScreen'
import TableLoader from 'common/components/loaders/TableLoader'
import Error from 'common/components/table/Error'
import Table, {
  OnCellChange,
  TableHandle,
  TableProps,
} from 'common/components/table/Table'
import Widget from 'common/components/Widget'
import WidgetHeader from 'common/components/WidgetHeader'
import { WidgetFirstUse } from 'common/components/WidgetMessage'

import TableWidgetMenu from '../menu/TableWidgetMenu'

export type TableWidgetProps<T extends object> = Omit<
  TableProps<T>,
  'data' | 'title'
> & {
  data?: readonly T[]
  isLoading?: boolean
  hasError?: boolean
  fileName?: string
  headerClassName?: string
  firstUseText?: ReactNode
  initialState?: Partial<TableState<Object>>
  onSelectRow?: (rowIds: T[]) => void
  onRowClick?: (rowId: T) => void
  onCellChange?: OnCellChange<T>
  selectedRowsActionButtons?: ReactNode
  children?: ReactNode
  rowSelection?: 'single' | 'multiple' | 'disabled'
  selectionHeader?: string
  disableSubRowArrow?: boolean
  paginateExpandedRows?: boolean
  generateRowClassName?: <T extends object>({
    row,
    rows,
  }: {
    row: Row<T>
    rows: Row<T>[]
    isHorizontalSeparatorHidden?: boolean
  }) => string
  cacheId?: string
}

const TableWidgetContext = React.createContext<{
  tableHandle: TableHandle<any> | undefined
  allValues: string[]
  onFilterValueChange: (value: string) => void
  onFullscreenClick: () => void
  selectedRowsActionButtons?: ReactNode
}>({
  tableHandle: undefined,
  allValues: [],
  onFilterValueChange: noop,
  onFullscreenClick: noop,
})

const TableWidget = <T extends object>({
  className,
  isLoading = false,
  hasError = false,
  data,
  columns,
  disablePagination,
  fileName,
  firstUseText,
  initialState,
  onSelectRow,
  onSwapColumns,
  onRowClick,
  onCellChange,
  selectedRowsActionButtons,
  autoResetPage = true,
  isHeaderHidden,
  rowSelection = 'disabled',
  selectionHeader,
  children,
  'data-cy': dataCy,
  'data-testid': dataTestId,
  disableSubRowArrow,
  generateRowClassName,
  paginateExpandedRows,
  headerClassName,
  isHorizontalSeparatorHidden,
  cacheId,
  manualSortBy,
  onSortChange,
}: TableWidgetProps<T>) => {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const [globalFilterValue, setGlobalFilterValue] = useState('')

  const getState = () => {
    if (hasError) {
      return 'error'
    } else if (data && isFullscreen && !isLoading) {
      return 'fullScreenTable'
    } else if (data && data.length === 0 && !isLoading) {
      return 'noData'
    } else if (data && data.length !== 0) {
      return 'table'
    } else {
      return 'default'
    }
  }

  const state = getState()

  const [tableHandle, setTableHandle] = useState<TableHandle<T>>()

  const ref: React.Ref<TableHandle<T>> = useCallback((node) => {
    if (node !== null) {
      setTableHandle(node)
    }
  }, [])

  const debounceTimer = useRef<ReturnType<typeof setTimeout>>()
  const onChange = useCallback((value: string) => {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => setGlobalFilterValue(value), 200)
  }, [])

  const [allValues, setAllValues] = useState<string[]>([])
  // Stable identity + functional update: comparing against a captured
  // `allValues` and depending on it recreated this callback on every state
  // change, which re-triggered Table's effect and looped render → setState →
  // render until React error #185. Returning `prev` bails out of the update.
  const handleAllFilterValues = useCallback((values: string[]) => {
    setAllValues((prev) => (isEqual(prev, values) ? prev : values))
  }, [])
  const onFullscreenClick = useCallback(() => {
    setIsFullscreen(true)
  }, [])
  const context = useMemo(
    () => ({
      tableHandle,
      allValues,
      onFilterValueChange: onChange,
      onFullscreenClick,
      selectedRowsActionButtons: selectedRowsActionButtons,
    }),
    [
      allValues,
      onChange,
      onFullscreenClick,
      selectedRowsActionButtons,
      tableHandle,
    ]
  )

  let actionButtons: ReactNode = null
  const filteredChildren = React.Children.toArray(children).filter((child) => {
    if (React.isValidElement(child) && child.type === ActionButtons) {
      actionButtons = child

      return false
    }
    return true
  })

  const customGlobalFilter = useCallback(
    (rows, columnIds, filterValue) => {
      if (!filterValue) {
        return rows
      }

      const filterableColumns = columnIds.filter((columnId) => {
        const column = columns.find(
          (col) => col.accessor === columnId || col.id === columnId
        )
        return column && !column.disableFilters
      })

      return rows.filter((row) =>
        filterableColumns.some((columnId) =>
          String(row.values[columnId])
            .toLowerCase()
            .includes(filterValue.toLowerCase())
        )
      )
    },
    [columns]
  )

  return (
    <Widget
      data-cy={dataCy}
      data-testid={dataTestId}
      className={cx(className, { 'border-red-500': state === 'error' })}
      type="table"
    >
      <TableWidgetContext.Provider value={context}>
        {filteredChildren}
        {
          {
            error: <Error />,
            fullScreenTable: (
              <FullScreen onClose={() => setIsFullscreen(false)}>
                {selectedRowsActionButtons ? (
                  <div className="text-coolGray-800 font-medium text-xl mb-4 ml-6 capitalize-first absolute top-6">
                    {selectedRowsActionButtons}
                  </div>
                ) : undefined}
                <Table
                  columns={columns}
                  data={data!}
                  disablePagination={disablePagination}
                  initialState={initialState}
                  onSelectRow={onSelectRow}
                  onRowClick={onRowClick}
                  onCellChange={onCellChange}
                  autoResetPage={autoResetPage}
                  onSwapColumns={onSwapColumns}
                  globalFilter={customGlobalFilter}
                  globalFilterValue={globalFilterValue}
                  updateAllFilterValues={handleAllFilterValues}
                  isHeaderHidden={isHeaderHidden}
                  rowSelection={rowSelection}
                  disableSubRowArrow={disableSubRowArrow}
                  generateRowClassName={generateRowClassName}
                  paginateExpandedRows={paginateExpandedRows}
                  headerClassName={headerClassName}
                  isHorizontalSeparatorHidden={isHorizontalSeparatorHidden}
                  cacheId={cacheId}
                  data-testid={`${dataTestId}-table`}
                  manualSortBy={manualSortBy}
                  onSortChange={onSortChange}
                />
              </FullScreen>
            ),
            table: (
              <div className={cx({ 'pb-4': disablePagination }, 'relative')}>
                {isLoading && (
                  <div className="absolute inset-0 z-10 h-full">
                    <TableLoader />
                  </div>
                )}
                <Table
                  ref={ref}
                  columns={columns}
                  data={data!}
                  disablePagination={disablePagination}
                  fileName={fileName}
                  initialState={initialState}
                  onSelectRow={onSelectRow}
                  onRowClick={onRowClick}
                  onCellChange={onCellChange}
                  autoResetPage={autoResetPage}
                  onSwapColumns={onSwapColumns}
                  globalFilter={customGlobalFilter}
                  globalFilterValue={globalFilterValue}
                  updateAllFilterValues={handleAllFilterValues}
                  isHeaderHidden={isHeaderHidden}
                  rowSelection={rowSelection}
                  selectionHeader={selectionHeader}
                  disableSubRowArrow={disableSubRowArrow}
                  generateRowClassName={generateRowClassName}
                  paginateExpandedRows={paginateExpandedRows}
                  headerClassName={headerClassName}
                  isHorizontalSeparatorHidden={isHorizontalSeparatorHidden}
                  cacheId={cacheId}
                  data-testid={`${dataTestId}-table`}
                  manualSortBy={manualSortBy}
                  onSortChange={onSortChange}
                />
              </div>
            ),
            noData:
              typeof firstUseText === 'string' || firstUseText === undefined ? (
                <WidgetFirstUse className="m-6" text={firstUseText} />
              ) : (
                firstUseText
              ),
            default: isLoading ? <TableLoader /> : null,
          }[state]
        }
        {actionButtons}
      </TableWidgetContext.Provider>
    </Widget>
  )
}

const TableWidgetMemo = memo(TableWidget) as typeof TableWidget

const TableWidgetHeader = ({
  title,
  subtitle,
  titleClassName,
  children,
}: {
  title?: ReactNode
  titleClassName?: string
  subtitle?: string
  children?: ReactNode
}) => {
  const { selectedRowsActionButtons } = useContext(TableWidgetContext)
  return (
    <WidgetHeader
      className={cx('pt-6 px-6', titleClassName)}
      title={selectedRowsActionButtons ? selectedRowsActionButtons : title}
      subtitle={subtitle}
      actions={<div className="inline-flex">{children}</div>}
    />
  )
}

type FilterProps = {
  placeholder?: string
}

const Filter = ({ placeholder = 'Filter' }: FilterProps) => {
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const { allValues, onFilterValueChange } = useContext(TableWidgetContext)

  const onSearch = (search: string) => {
    setSearchValue(search)
    onFilterValueChange(search)
  }

  useEffect(() => {
    if (searchValue === undefined) {
      onFilterValueChange('')
    }
  }, [searchValue, onFilterValueChange])

  return (
    <Autocomplete
      className="w-60"
      placeholder={placeholder}
      data-cy="table-widget-search"
      data-testid="table-widget-search"
      options={[...new Set(allValues)].filter(String).map((option) => ({
        label: option,
        value: option,
      }))}
      value={
        searchValue ? { value: searchValue, label: searchValue } : undefined
      }
      defaultInputValue={searchValue}
      onSearch={onSearch}
      backspaceRemovesValue={true}
    />
  )
}

export function ButtonWithResetPaging({ onClick, ...others }: ButtonProps) {
  const { tableHandle } = useContext(TableWidgetContext)
  return (
    <Button
      {...others}
      onClick={(values) => {
        tableHandle?.resetPaging()
        onClick?.(values)
      }}
    />
  )
}

const Menu = ({ hasCsvExport }: { hasCsvExport?: boolean }) => {
  const { tableHandle, onFullscreenClick } = useContext(TableWidgetContext)
  return (
    <TableWidgetMenu
      onFullscreenClick={onFullscreenClick}
      exportCsv={
        hasCsvExport
          ? () => hasCsvExport && tableHandle?.exportCsv()
          : undefined
      }
      columns={tableHandle?.allColumns()}
    />
  )
}

const ActionButtons = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return <div className={cx('flex self-end p-6', className)}>{children}</div>
}

const TableWidgetComponents = {
  Widget: TableWidgetMemo,
  Header: TableWidgetHeader,
  Filter,
  Menu,
  ActionButtons,
}

export default TableWidgetComponents
